import { Component, useCallback, useEffect, useState, type ReactNode } from 'react'
import { AppBar, type Tab } from './components/AppBar'
import { loadUserName } from './lib/session'
import { initialScreen, urlFor, type InitialScreen } from './lib/startup'
import { NewOrder } from './screens/NewOrder'
import { NotFound } from './screens/NotFound'
import { OrderDetail } from './screens/OrderDetail'
import { OrderHistory } from './screens/OrderHistory'

/** No router: the screen is App state, mirrored into the address bar (`?order=`, `?view=history`). */
type View = InitialScreen | { screen: 'notFound' }

function viewOf(location: Location): View {
  // The app is served from the root only; any other path is a stale or mistyped link.
  if (location.pathname !== '/' && location.pathname !== '/index.html') return { screen: 'notFound' }
  return initialScreen(location.search)
}

const keyOf = (v: View): string => (v.screen === 'detail' ? `detail:${v.orderId}:${v.submitted}` : v.screen)

export default function App() {
  const [view, setView] = useState<View>(() => viewOf(window.location))
  const [userName, setUserName] = useState<string | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    void loadUserName().then((name) => {
      if (!cancelled) setUserName(name)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const onPop = () => setView(viewOf(window.location))
    const onError = () => setError(true)
    window.addEventListener('popstate', onPop)
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onError)
    return () => {
      window.removeEventListener('popstate', onPop)
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onError)
    }
  }, [])

  const navigate = useCallback((next: InitialScreen) => {
    window.history.pushState(null, '', urlFor(next))
    setView(next)
    window.scrollTo(0, 0)
  }, [])

  const toTab = useCallback((tab: Tab) => navigate({ screen: tab }), [navigate])

  const active: Tab | null = view.screen === 'new' || view.screen === 'history' ? view.screen : null

  let screen: ReactNode
  switch (view.screen) {
    case 'new':
      screen = <NewOrder onSubmitted={(orderId) => navigate({ screen: 'detail', orderId, submitted: true })} />
      break
    case 'history':
      screen = <OrderHistory onOpen={(orderId) => navigate({ screen: 'detail', orderId, submitted: false })} />
      break
    case 'detail':
      screen = <OrderDetail orderId={view.orderId} submitted={view.submitted} onNavigate={toTab} />
      break
    default:
      screen = <NotFound onHome={() => toTab('new')} />
  }

  return (
    <>
      <AppBar active={active} userName={userName} onNavigate={toTab} />
      <main>
        <ErrorBoundary key={keyOf(view)} onError={() => setError(true)}>
          {screen}
        </ErrorBoundary>
      </main>
      {error && <ErrorToast onDismiss={() => setError(false)} />}
    </>
  )
}

/** The Blazor `#blazor-error-ui` snackbar, kept with its look. */
function ErrorToast({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div id="blazor-error-ui" className="show" role="alert">
      An unhandled error has occurred.
      <a href="." className="reload">
        Reload
      </a>
      <button type="button" className="dismiss" aria-label="Dismiss" onClick={onDismiss}>
        🗙
      </button>
    </div>
  )
}

class ErrorBoundary extends Component<{ children: ReactNode; onError: () => void }, { failed: boolean }> {
  state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  componentDidCatch(error: unknown) {
    // The type only: a render error's message can carry form data.
    console.error('Unhandled render error', error instanceof Error ? error.name : typeof error)
    this.props.onError()
  }

  render() {
    return this.state.failed ? null : this.props.children
  }
}
